/**
 * Shared List-B membership predicate (P3 merged lens, 2026-07-09).
 *
 * PURE — no Firebase, no React, relative-import-free — so BOTH readers
 * (`subscribeContactNotesEmail` and `subscribeProjectNotesEmail` in
 * contactThread.ts) import the SAME rule and can never diverge again, and the
 * unit test exercises it directly.
 *
 * A `projects/{pid}/messages` doc belongs to List B — the NON-WhatsApp set
 * (internal notes · email · system events · AI drafts) that the merged lens
 * reads OUTSIDE the live List-A WhatsApp thread — if and only if ALL hold:
 *
 *   1. it carries NO `waMessageId`.  ← the data-driven WhatsApp discriminator.
 *      Production forensics (337-doc sample): every WhatsApp doc carries a
 *      `waMessageId` under BOTH channel spellings seen live — `whatsapp_cloud`
 *      (209) and `wa` (115) — while NO channel-less note ever does. Using the
 *      wamid (not a channel allow/deny value) survives any new channel spelling
 *      and prevents the 209 `whatsapp_cloud` docs from being duplicated against
 *      List A.
 *   2. `channel` is not `'wa'` / `'whatsapp_cloud'`.  ← belt-and-braces.
 *   3. it is not a crossRef mirror (`type === 'crossRef'` OR any
 *      `sourceMessageId`) — the project mirror of an already-shown message.
 *
 * NOTE the deny-list is on the WhatsApp identity, NOT an `in ['portal','email']`
 * allow-list: channel-less legacy staff notes / system docs (12 in the sample,
 * rendered by the pre-change ProjectThreadPanel as `channel ?? 'portal'`) are
 * KEPT — nothing the user saw before disappears.
 */
export interface ListBCandidate {
  waMessageId?: unknown;
  channel?: string | null;
  type?: string | null;
  sourceMessageId?: unknown;
}

export function isListBMessage(data: ListBCandidate): boolean {
  // 1) A WhatsApp message id → it's WhatsApp, already live in List A.
  if (data.waMessageId) return false;
  // 2) Belt-and-braces on the known WhatsApp channel spellings.
  if (data.channel === 'wa' || data.channel === 'whatsapp_cloud') return false;
  // 3) crossRef mirror guard (a mirror re-shows a message we already have).
  if (data.type === 'crossRef') return false;
  if (data.sourceMessageId) return false;
  return true;
}
